import React, { useState, useEffect, useMemo } from "react";
import {
  DataGridPro,
  useGridApiRef,
  GridColDef,
} from "@mui/x-data-grid-pro"
import { useTranslation } from "react-i18next";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import { getSimulator, ISimulatorRow } from "../../../../../webapi/service/getSimulator";
import {
  AppConfig,
  ProjectStatus,
} from "../../../../../common/constants";
import { ISelectSimulatorParam } from "../../../../../webapi/constants";
import { deleteSimulators } from "../../../../../webapi/service/deleteSimulators";
import { IFileRow } from "../../../../../webapi/service/getFIles";
import AuthService from "../../../../../common/AuthService";
import BackButton from "../../../common/BackButton";
import ReloadButton from "../../../../common/ReloadButton";
import DeleteButton from "../../../common/DeleteButton";
import { getProjects, IProjectRow } from "../../../../../webapi/service/getProjects";

import "../../../../main.css";
import "../../../../../App.css";
import { Button } from "react-bootstrap";
import SimulationSettingDialog from "./SimulationSettingDialog"
import SimulationSettingNofile from "./SimulationSettingNofile";
import { simulateSimulator } from "../../../../../webapi/service/simulateSimulator";
import { getFileLists } from "../../../../../webapi/service/getFileLists";


export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * プロジェクト一覧画面に戻る場合に呼び出します
   */
  onTop: () => void;
  /**
   * ファイル一覧をロードする場合に呼び出します
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoadFiles: (loading: boolean) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (param: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}
  
export default function SimList(props: IProps): JSX.Element {
  const StatusDoing = ProjectStatus.DOING;
  const StatusDone = ProjectStatus.DONE;
  const { i18n, t } = useTranslation();
  const apiRef = useGridApiRef();
  const [rows, setRows] = useState<ISimulatorRow[]>([]);
  const [buttonVisible, setButtonVisible] = useState<boolean>(false);
  const [buttonEnable, setButtonEnable] = useState<boolean>(false);
  const [showSimSettingDiag, setShowSimSettingDiag] = useState<boolean>(false);
  const [showNofileSettingDiag, setShowNofileSettingDiag] = useState<boolean>(false);
  const [filerows, setFilelows] = useState<IFileRow[]>([]);
  const {
    authService,
    projectInfo,
    onBack,
    onTop,
    onLoading,
    showMessage,
    showConfirm,
    onCommunicationError,
    resetUnoperatedTimeout,
  } = props;
 const projectid = projectInfo.projectid;

  useEffect(() => {
    loadSimulators();
  }, []);

  /**
   * チェックボックスのチェックが外されたときにボタンを非表示・無効にする
   */
  function clearCheckRows() {
    apiRef.current.selectRows(apiRef.current.getAllRowIds(), false);
    setButtonVisible(false);
    setButtonEnable(false);
  }

  function loadSimulators() {  
    onLoading(true);
    clearCheckRows();
    getSimulator({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        setRows(rows);
        setTimeout(() => {
          onLoading(false);
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleReload() {
    resetUnoperatedTimeout();
    loadSimulators();
  }

  function deleteSimulator_Internal(selectSimulators: ISelectSimulatorParam[]) {
    onLoading(true);
    resetUnoperatedTimeout();
    deleteSimulators({
      authService: authService,
      simulators: selectSimulators,
      success: () => {
        onLoading(false);
        loadSimulators();
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      },
    });
  }

  function handleOpenDeleteDialog() {
    const simulators: ISelectSimulatorParam[] = [];
    const map = apiRef.current.getSelectedRows();
    map.forEach((value) => {
      simulators.push({ projectid: value.projectid, simulatorid: value.simulatorid });
    });
    showConfirm({
      title: t("simulator_delete_confirm"),
      message: t("simulator_delete_confirm_message"),
      onOk: () => deleteSimulator_Internal(simulators),
    });
  }

  function handleCreate() {
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setTimeout(() => {
          if (row.simcreatests === StatusDoing) {
            showMessage({
              title: t("simcreatests_doing"),
              message: t("simcreatests_doing_message"),
            });
          } else if (row.simevalsts === StatusDoing) {
            showMessage({
              title: t("simevalsts_doing"),
              message: t("simevalsts_doing_message"),
            });
          } else if (row.fkdpplearnsts === StatusDoing) {
            showMessage({
              title: t("fkdpplearnsts_doing"),
              message: t("fkdpplearnsts_doing_message"),
            });
          } else if (row.fkdppevalsts === StatusDoing) {
            showMessage({
              title: t("fkdppevalsts_doing"),
              message: t("fkdppevalsts_doing_message"),
            });
          } else if (row.fkdppsimsts === StatusDoing) {
            showMessage({
              title: t("fkdppsimsts_doing"),
              message: t("fkdppsimsts_doing_message"),
            });
          } else if (row.simevalsts === StatusDone) {
            showConfirm({
              title: t("simulator_simulation_overwrite"),
              message: t("simulator_simulation_overwrite_message"),
              onOk: () => {
                handleOpenSimulationSettingDialog();
              },
            });
          } else {
            handleOpenSimulationSettingDialog();
          }
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleOpenSimulationSettingDialog() {
    resetUnoperatedTimeout();
    onLoading(true);
    getFileLists({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        setFilelows(rows);
        setTimeout(() => {
          onLoading(false);
          if (rows.length === 0) {
            showConfirm({
              title: t("simulator_simulation_nofile"),
              message: t("simulator_simulation_nofile_message"),
              onOk: () => {
                setShowNofileSettingDiag(true);
              },
            });
          } else {
            setShowSimSettingDiag(true);
          }
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleSimulationSettingCancel() {
    setShowSimSettingDiag(false);
  }

  function handleNofileSettingCancel() {
    setShowNofileSettingDiag(false);
  }

  function handleSimulationSettingOk(step: boolean, impulse: boolean, files?: string[]) {
    const simulators: string[] = [];
    let comparisonfiles: string[]|undefined = [];
    const map = apiRef.current.getSelectedRows();
    map.forEach((value) => {
      simulators.push(value.simulatorid);
    })
    comparisonfiles = files?.length !== 0 ? files : undefined;
    onLoading(true);
    handleSimulationSettingCancel();
    handleNofileSettingCancel();
    resetUnoperatedTimeout();
    simulateSimulator({
      authService: authService,
      projectid: projectInfo.projectid,
      impulse: impulse,
      step: step,
      comparisonfiles: comparisonfiles,
      simulators: simulators,
      success: () => {
        onTop();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  const simSettingDiag = showSimSettingDiag ? (
    <SimulationSettingDialog
      title={t("simulator_simulation_title")}
      caption={t("simulator_simulation_caption")}
      show={showSimSettingDiag}
      simulators={rows}
      files={filerows}
      onOk={handleSimulationSettingOk}
      onCancel={handleSimulationSettingCancel}
    />
  ) : (
    ""
  );

  const nofileSettingDiag = showNofileSettingDiag ? (
    <SimulationSettingNofile
      title={t("simulator_simulation_title")}
      show={showNofileSettingDiag}
      simulators={rows}
      onOk={handleSimulationSettingOk}
      onCancel={handleNofileSettingCancel}
    />
  ) : (
    ""
  );

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "simulatorid",
        headerName: t("simulator_list_id"),
        width: 200,
        hide: true,
      },
      {
        field: "name",
        headerName: t("simulator_list_name"),
        width: 200,
        sortable: true,
      },
      {
        field: "simcreatetime",
        headerName: t("simulator_list_time"),
        width: 200,
        sortable: true,
      },
    ];
  }, [i18n.language]);

  /**
   * 削除ボタン
   */
  const deleteBtn = buttonVisible ? (
    <DeleteButton onClick={handleOpenDeleteDialog} />
  ) : (
    ""
  );

  /**
   * シミュレーションボタン
   */
  const simulationBtn = buttonEnable ? (
    <Button className="btn-mainspace" onClick={handleCreate}>
      {t("simulator_simulation")}
    </Button>
  ) : (
    <Button className="btn-mainspace" disabled>
      {t("simulator_simulation")}
    </Button>
  );
  
  /**
   * 表の構成
   */
  const dataGrid = useMemo(() => {
    return (
      <DataGridPro
        columns={columns}
        rows={rows}
        checkboxSelection
        disableSelectionOnClick
        disableColumnMenu
        onSelectionModelChange={(selectionModel) => {
          setButtonVisible(selectionModel.length > 0 ? true : false);
          setButtonEnable(
            (selectionModel.length > 0) && (selectionModel.length <= AppConfig.MAX_SIM_SIMULATION)
            ? true : false );
        }}
        getRowClassName={() => {
          return "grid-row";
        }}
        apiRef={apiRef}
        localeText={{
          noRowsLabel: "",
          footerTotalRows: t("simulator_list_total_rows") + " : ",
          footerRowSelected: (count) => t("list_total_selecedtitem") + " : " + count,
        }}
        isRowSelectable={(params) => {
          return params.row.projectid.length > 0;
        }}
      />
    );
  }, [rows, i18n.language]);

  return (
    <React.Fragment>
      <div className="mainspace">
        <div className="mainspace-title">
          <h3>{t("project_detail_project_name")}</h3>
          <h3 style={{ marginLeft: "10px" }}>{projectInfo.name}</h3>
        </div>
        <div className="mainspace-toolbar">
          <BackButton onClick={onBack} />
          <ReloadButton onClick={handleReload} />
          {deleteBtn}
          {simulationBtn}
        </div>
        <div className="mainspace-toolbar">
          <h3>{t("simulator_list")}</h3>
        </div>
        <div className="projectlist-grid">{dataGrid}</div>
        {simSettingDiag}
        {nofileSettingDiag}
      </div>
    </React.Fragment>
  );
}

